import { useReducer } from 'react'
import type { CheckoutShippingMethodCode } from '../types'
import type { CheckoutFlowAction, CheckoutFlowState } from './CheckoutFlow.types'
import { EMPTY_ADDRESS_FORM } from './CheckoutFlow.types'

const DEFAULT_SHIPPING_METHOD: CheckoutShippingMethodCode = 'standard'

const createInitialState = (): CheckoutFlowState => ({
  bootstrapRequestId: 0,
  isBootstrapping: true,
  isSubmitting: false,
  isRedirecting: false,
  sessionId: null,
  session: null,
  review: null,
  shippingMethods: [],
  step: 'cart_review',
  sameAsShipping: true,
  shippingAddressForm: EMPTY_ADDRESS_FORM,
  billingAddressForm: EMPTY_ADDRESS_FORM,
  selectedShippingMethod: DEFAULT_SHIPPING_METHOD,
  paymentProviders: [],
  selectedPaymentProvider: 'bank_transfer',
  lastConfirmResult: null,
  errorCode: null,
  errorDetail: '',
  validationErrors: [],
})

const reducer = (state: CheckoutFlowState, action: CheckoutFlowAction): CheckoutFlowState => {
  switch (action.type) {
    case 'BOOTSTRAP_STARTED':
      return {
        ...state,
        isBootstrapping: true,
        errorCode: null,
        errorDetail: '',
      }
    case 'BOOTSTRAP_SUCCEEDED':
      return {
        ...state,
        isBootstrapping: false,
        sessionId: action.session.id,
        session: action.session,
        shippingAddressForm: action.shippingAddressForm,
        billingAddressForm: action.billingAddressForm,
        selectedShippingMethod: action.selectedShippingMethod,
        selectedPaymentProvider: action.selectedPaymentProvider,
        step: action.step,
        sameAsShipping: action.session.sameAsShipping,
        errorCode: null,
        errorDetail: '',
      }
    case 'BOOTSTRAP_FAILED':
      return {
        ...state,
        isBootstrapping: false,
        errorCode: action.code,
        errorDetail: action.detail ?? '',
      }
    case 'RETRY_BOOTSTRAP':
      return {
        ...state,
        bootstrapRequestId: state.bootstrapRequestId + 1,
        isBootstrapping: true,
        errorCode: null,
        errorDetail: '',
      }
    case 'SET_STEP':
      return {
        ...state,
        step: action.step,
      }
    case 'SET_ERROR':
      return {
        ...state,
        errorCode: action.code,
        errorDetail: action.detail ?? '',
      }
    case 'CLEAR_ERROR':
      return {
        ...state,
        errorCode: null,
        errorDetail: '',
      }
    case 'SET_VALIDATION_ERRORS':
      return {
        ...state,
        validationErrors: action.validationErrors,
      }
    case 'CLEAR_VALIDATION_ERRORS':
      return {
        ...state,
        validationErrors: [],
      }
    case 'SET_SAME_AS_SHIPPING':
      return {
        ...state,
        sameAsShipping: action.value,
        billingAddressForm: action.value ? state.shippingAddressForm : state.billingAddressForm,
      }
    case 'SET_SHIPPING_FORM':
      return {
        ...state,
        shippingAddressForm: action.value,
        billingAddressForm: state.sameAsShipping ? action.value : state.billingAddressForm,
      }
    case 'SET_BILLING_FORM':
      return {
        ...state,
        billingAddressForm: action.value,
      }
    case 'SET_SHIPPING_FIELD': {
      const nextShipping = {
        ...state.shippingAddressForm,
        [action.field]: action.value,
      }
      return {
        ...state,
        shippingAddressForm: nextShipping,
        billingAddressForm: state.sameAsShipping ? nextShipping : state.billingAddressForm,
      }
    }
    case 'SET_BILLING_FIELD':
      return {
        ...state,
        billingAddressForm: {
          ...state.billingAddressForm,
          [action.field]: action.value,
        },
      }
    case 'SET_SELECTED_SHIPPING_METHOD':
      return {
        ...state,
        selectedShippingMethod: action.value,
      }
    case 'SET_SELECTED_PAYMENT_PROVIDER':
      return {
        ...state,
        selectedPaymentProvider: action.value,
      }
    case 'SESSION_UPDATED':
      return {
        ...state,
        sessionId: action.session.id,
        session: action.session,
        sameAsShipping: action.session.sameAsShipping,
        shippingAddressForm: action.shippingAddressForm,
        billingAddressForm: action.billingAddressForm,
        selectedShippingMethod: action.selectedShippingMethod,
        selectedPaymentProvider:
          action.selectedPaymentProvider
          ?? action.session.selectedPaymentProvider
          ?? state.selectedPaymentProvider,
        step: action.step,
      }
    case 'REVIEW_UPDATED':
      return {
        ...state,
        review: action.review,
        shippingMethods: action.shippingMethods,
        paymentProviders: action.paymentProviders,
        selectedPaymentProvider:
          action.paymentProviders.some((provider) => provider.id === state.selectedPaymentProvider && provider.enabled)
            ? state.selectedPaymentProvider
            : action.defaultPaymentProvider,
      }
    case 'CONFIRM_COMPLETED':
      return {
        ...state,
        isRedirecting: false,
        isSubmitting: false,
        lastConfirmResult: action.result,
      }
    case 'SUBMIT_STARTED':
      return {
        ...state,
        isSubmitting: true,
        errorCode: null,
        errorDetail: '',
      }
    case 'SUBMIT_COMPLETED':
      return {
        ...state,
        isSubmitting: false,
      }
    case 'REDIRECT_STARTED':
      return {
        ...state,
        isRedirecting: true,
        isSubmitting: false,
        step: 'redirecting_to_payment',
      }
    default:
      return state
  }
}

export const useCheckoutFlowReducer = () => useReducer(reducer, undefined, createInitialState)
